import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";

/** The open pane lives on the URL, e.g. `/sessions/abc?pane=diff&paneId=att_123`. */
export const WORKSPACE_PANE_QUERY_PARAM = "pane";
export const WORKSPACE_PANE_ID_QUERY_PARAM = "paneId";
export const WORKSPACE_PANE_TYPES = ["diff", "canvas"] as const;
export type WorkspacePaneType = (typeof WORKSPACE_PANE_TYPES)[number];
export interface WorkspacePaneState {
  type: WorkspacePaneType;
  id?: string;
}
interface PendingPaneState {
  pane: WorkspacePaneState | null;
  url: string;
  sequence: number;
}
function first(value: string | string[] | undefined): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}
function pathnameOf(url: string): string {
  return url.split(/[?#]/, 1)[0];
}
export function parseWorkspacePaneType(value: string | string[] | undefined): WorkspacePaneType | null {
  const raw = first(value);
  return WORKSPACE_PANE_TYPES.includes(raw as WorkspacePaneType) ? raw as WorkspacePaneType : null;
}

/** URL-backed pane state with an optimistic value while Next's shallow route settles. */
export function useWorkspacePaneUrlState() {
  const $ = _c(32);
  const router = useRouter();
  const [localPane, setLocalPane] = useState(null);
  const [pending, setPending] = useState();
  const sequence = useRef(0);
  let t0;
  if ($[0] !== router) {
    t0 = router ? parseWorkspacePaneType(router.query[WORKSPACE_PANE_QUERY_PARAM]) : null;
    $[0] = router;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const routeType = t0;
  let t1;
  if ($[2] !== routeType || $[3] !== router) {
    t1 = router && routeType !== null ? first(router.query[WORKSPACE_PANE_ID_QUERY_PARAM]) ?? null : null;
    $[2] = routeType;
    $[3] = router;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const routeId = t1;
  let t2;
  if ($[5] !== router) {
    t2 = router ? pathnameOf(router.asPath) : null;
    $[5] = router;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const routePathname = t2;
  let t3;
  let t4;
  if ($[7] !== pending || $[8] !== routeId || $[9] !== routePathname || $[10] !== routeType) {
    t3 = () => {
      if (!pending) {
        return;
      }
      const target = pending.pane;
      const settled = target === null ? routeType === null : routeType === target.type && routeId === (target.id ?? null);
      const leftPage = routePathname !== null && routePathname !== pathnameOf(pending.url);
      if (settled || leftPage) {
        setPending(undefined);
      }
    };
    t4 = [pending, routeType, routeId, routePathname];
    $[7] = pending;
    $[8] = routeId;
    $[9] = routePathname;
    $[10] = routeType;
    $[11] = t3;
    $[12] = t4;
  } else {
    t3 = $[11];
    t4 = $[12];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[13] !== router) {
    t5 = () => {
      if (!router?.events) {
        return;
      }
      const settle = () => setPending(undefined);
      const settleUrl = url => {
        setPending(current => current?.url === url ? undefined : current);
      };
      const settleError = (_error, url_0) => settleUrl(url_0);
      router.events.on("routeChangeComplete", settleUrl);
      router.events.on("routeChangeError", settleError);
      window.addEventListener("popstate", settle);
      return () => {
        router.events.off("routeChangeComplete", settleUrl);
        router.events.off("routeChangeError", settleError);
        window.removeEventListener("popstate", settle);
      };
    };
    t6 = [router];
    $[13] = router;
    $[14] = t5;
    $[15] = t6;
  } else {
    t5 = $[14];
    t6 = $[15];
  }
  useEffect(t5, t6);
  let t7;
  if ($[16] !== localPane || $[17] !== pending || $[18] !== routeId || $[19] !== routeType || $[20] !== router) {
    t7 = router ? pending ? pending.pane : routeType === null ? null : {
      type: routeType,
      id: routeId ?? undefined
    } : localPane;
    $[16] = localPane;
    $[17] = pending;
    $[18] = routeId;
    $[19] = routeType;
    $[20] = router;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const effective = t7;
  const type = effective?.type ?? null;
  const id = effective?.id ?? null;
  let t8;
  if ($[22] !== router) {
    t8 = (next, options) => {
      if (!router) {
        setLocalPane(next);
        return;
      }
      const params = new URLSearchParams(window.location.search);
      const openInUrl = parseWorkspacePaneType(params.get(WORKSPACE_PANE_QUERY_PARAM) ?? undefined) !== null;
      if (next === null) {
        params.delete(WORKSPACE_PANE_QUERY_PARAM);
        params.delete(WORKSPACE_PANE_ID_QUERY_PARAM);
      } else {
        params.set(WORKSPACE_PANE_QUERY_PARAM, next.type);
        if (next.id === undefined) {
          params.delete(WORKSPACE_PANE_ID_QUERY_PARAM);
        } else {
          params.set(WORKSPACE_PANE_ID_QUERY_PARAM, next.id);
        }
      }
      const query = params.toString();
      const url_1 = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
      const nextSequence = sequence.current = sequence.current + 1;
      setPending({
        pane: next,
        url: url_1,
        sequence: nextSequence
      });
      const navigate = !openInUrl && next !== null && !options?.replace ? router.push(url_1, undefined, {
        shallow: true,
        scroll: false
      }) : router.replace(url_1, undefined, {
        shallow: true,
        scroll: false
      });
      Promise.resolve(navigate).then(completed => {
        if (completed) {
          return;
        }
        setPending(current_0 => current_0?.sequence === nextSequence ? undefined : current_0);
      }).catch(() => {
        setPending(current_1 => current_1?.sequence === nextSequence ? undefined : current_1);
      });
    };
    $[22] = router;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const setPane = t8;
  let t9;
  if ($[24] !== setPane || $[25] !== type) {
    t9 = pane => setPane(type === null ? pane : null);
    $[24] = setPane;
    $[25] = type;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const togglePane = t9;
  let t10;
  if ($[27] !== id || $[28] !== setPane || $[29] !== togglePane || $[30] !== type) {
    t10 = {
      type,
      id,
      setPane,
      togglePane
    };
    $[27] = id;
    $[28] = setPane;
    $[29] = togglePane;
    $[30] = type;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
}